<template>
  <div class="max-w-[720px] w-full px-4 sm:px-0 sm:mr-5">
    <h2 class="w-full text-center mb-8">
      {{ $t('messages.menu.management') }}
    </h2>
    <div class="flex flex-col border border-gray-200 rounded-lg sm:ml-10 bg-white min-h-[240px]">
      <div class="flex justify-between items-start">
        <h3 class="ml-6 mt-6 mb-6 text-zyxel-gray-900 font-bold">
          {{ $t('messages.management.account_parameters') }}
        </h3>
        <div class="mr-6 mt-6 mb-6 md:block">
          <div v-if="!isEditing">
            <button
              class="text-zyxel-green-700 font-bold cursor-pointer"
              :class="{ 'cursor-not-allowed text-zyxel-gray-300': isDemoAccount }"
              :disabled="isDemoAccount"
              @click="toggleEdit"
            >
              {{ $t('messages.common.link_edit') }}
            </button>
          </div>
          <div
            v-else
            class="flex-row block"
          >
            <button
              class="text-zyxel-green-700 font-bold cursor-pointer"
              @click="cancelEdit"
            >
              {{ $t('messages.common.link_cancel') }}
            </button>
            <button
              class="text-zyxel-green-700 font-bold cursor-pointer ml-2"
              @click="saveEdit"
            >
              {{ $t('messages.common.link_save') }}
            </button>
          </div>
        </div>
      </div>
      <div>
        <div v-if="isEditing">
          <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            <dt
              class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-6 mb-5 h-12"
              for="session_timeout"
            >
              {{ $t('messages.management.session_timeout') }}
              ({{ $t('messages.management.seconds') }})<span class="ml-2 text-zyxel-red-700">*</span>
            </dt>
            <dd class="mr-6">
              <select
                id="session_timeout"
                v-model="edit_values.session_timeout"
                :class="['text-input p-2 text-left h-12 w-full', { 'error-input': errors.session_timeout }]"
              >
                <option value="300">
                  {{ formatDuration(5 * 60) }}
                </option>
                <option value="600">
                  {{ formatDuration(10 * 60) }}
                </option>
                <option value="1200">
                  {{ formatDuration(20 * 60) }}
                </option>
                <option value="1800">
                  {{ formatDuration(30 * 60) }}
                </option>
                <option value="3600">
                  {{ formatDuration(60 * 60) }}
                </option>
              </select>
            </dd>
            <dt
              class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-6 mb-5 h-12"
              for="sign_in_history"
            >
              {{ $t('messages.management.sign_in_history') }}
              ({{ $t('messages.management.days') }})<span class="ml-2 text-zyxel-red-700">*</span>
            </dt>
            <dd class="mr-6">
              <select
                id="sign_in_history"
                v-model="edit_values.sign_in_history"
                :class="['text-input p-2 text-left h-12 w-full', { 'error-input': errors.sign_in_history }]"
              >
                <option value="30">
                  {{ formatDuration(30 * 24 * 60 * 60) }}
                </option>
                <option value="60">
                  {{ formatDuration(60 * 24 * 60 * 60) }}
                </option>
                <option value="90">
                  {{ formatDuration(90 * 24 * 60 * 60) }}
                </option>
                <option value="120">
                  {{ formatDuration(120 * 24 * 60 * 60) }}
                </option>
                <option value="180">
                  {{ formatDuration(180 * 24 * 60 * 60) }}
                </option>
              </select>
            </dd>
          </dl>
        </div>
        <div v-else>
          <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            <dt class="text-zyxel-gray-900 max-w-[240px] ml-6 mb-5">
              {{ $t('messages.management.session_timeout') }}
            </dt>
            <dd>{{ formatDuration(session_timeout) }}</dd>
            <dt class="text-zyxel-gray-900 max-w-[240px] ml-6 mb-5">
              {{ $t('messages.management.sign_in_history') }}
            </dt>
            <dd>{{ formatDuration(sign_in_history * 24 * 60 * 60) }}</dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { useAuthStore } from '@/stores/auth';
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import { logger } from '@/utils/logger';

export default {
    name: 'SetSystemConfig',
    data() {
        return {
            isEditing: false,
            session_timeout: 30 * 60,
            sign_in_history: 30,
            edit_values: {
                session_timeout: 30 * 60,
                sign_in_history: 30,
            },
            errors: {
                session_timeout: false,
                sign_in_history: false
            }
        }
    },
    computed: {
        isDemoAccount() {
            const authStore = useAuthStore();
            return authStore.isDemoAccount;
        }
    },
    created() {
        this.fetchSystemConfig();
    },
    methods: {
        async fetchSystemConfig() {
            try {
                const response = await axios.get(`${apiBaseUrl}/api/system/management`);
                this.session_timeout = response.data.session_timeout;
                this.sign_in_history = response.data.sign_in_history;
                this.edit_values.session_timeout = response.data.session_timeout;
                this.edit_values.sign_in_history = response.data.sign_in_history;
            } catch (error) {
                logger.error("Error fetching system config:", error);
                if (error.response?.status === 401 || error.response?.status === 403) {
                    this.$router.push({ name: 'UserInfoPage' });
                }
            }
        },
        async updateSystemConfig() {
            try {
                await axios.post(`${apiBaseUrl}/api/system/management`, {
                    session_timeout: this.edit_values.session_timeout,
                    sign_in_history: this.edit_values.sign_in_history
                });
                this.session_timeout = this.edit_values.session_timeout;
                this.sign_in_history = this.edit_values.sign_in_history;
                this.isEditing = false;
            } catch (error) {
                logger.error("Error updating system config:", error);
                if (error.response?.status === 401 || error.response?.status === 403) {
                    this.$router.push({ name: 'UserInfoPage' });
                }
            }
        },
        toggleEdit() {
            if (this.isDemoAccount) {
                return;
            }
            if (!this.isEditing) {
                this.edit_values.session_timeout = this.session_timeout;
                this.edit_values.sign_in_history = this.sign_in_history;
            }
            this.isEditing = !this.isEditing;
        },
        async saveEdit() {
            if (!this.checkInputs()) {
                return;
            }
            this.session_timeout = this.edit_values.session_timeout;
            this.sign_in_history = this.edit_values.sign_in_history;

            await this.updateSystemConfig();

            this.isEditing = false;
        },
        cancelEdit() {
            this.clearErrors();
            this.isEditing = false;
        },
        checkInputs() {
            return true;
        },
        clearErrors() {
            this.errors.session_timeout = false;
            this.errors.sign_in_history = false;
        },
        formatDuration(seconds) {
            const days = Math.floor(seconds / (3600 * 24));
            seconds -= days * 3600 * 24;
            const hrs = Math.floor(seconds / 3600);
            seconds -= hrs * 3600;
            const mnts = Math.floor(seconds / 60);
            seconds -= mnts * 60;

            let result = [];
            if (days > 0) result.push(`${days} ` + this.$t("messages.management.days"));
            if (hrs > 0) result.push(`${hrs} ` + this.$t("messages.management.hours"));
            if (mnts > 0) result.push(`${mnts} ` + this.$t("messages.management.minutes"));
            if (seconds > 0) result.push(`${seconds} ` + this.$t("messages.management.seconds"));

            return result.join(' ');
        }
    },
}
</script>